import React, { useEffect, useContext, useState } from 'react';
import { getDatabase, ref, set, push, onValue } from "firebase/database";

import {
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputLabel,
  Box,
  FormControl,
  Select,
  Autocomplete,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox
} from '@mui/material';

import {
  Download as DownloadIcon,
} from '@mui/icons-material';

import { SideNavItem } from 'layouts/portal/side-nav-item';

import { Tracking } from 'components/Tracking';
import { AuthContext, stopPropagationForTab, get_csv_data, create_zip } from 'components';

const db = getDatabase();

let sortByTypeAndLabel = (a, b) => {
  // First, compare by type
  if (a.type < b.type) {
    return 1;
  } else if (a.type > b.type) {
    return -1;
  } else {
    // If types are equal, compare by label
    if (a.label < b.label) {
      return -1;
    } else if (a.label > b.label) {
      return 1;
    } else {
      return 0;
    }
  }
}


const CheckListItem = (props) => {
  const { id, title, value, onChange } = props;

  return (
    <ListItem
      key={value}
      disablePadding
      sx={{ p: 0, mt: 0, mb: 0 }}
    >
      <ListItemButton
        onClick={(e) => { 
          if (onChange) {
            onChange(!value)
          }
        }}
        sx={{ p: 0 }}
      >
        <Checkbox
          id={id}
          key={id}
          sx={{ pt: 0, pb: 0 }}
          edge="start"
          checked={value}
        />
        <ListItemText primary={title} />
      </ListItemButton>
    </ListItem>
  )
}

const ALL_BRANDS = {
  type: 'All',
  label: 'Export All Brands',
  id: 'all'
}


export const ExportCSV = (props) => {
  const { onClose } = props;
  const [ open, setOpen ] = useState(false);
  const [ seasonLimit, setSeasonLimit ] = useState(3);
  const [ saving, setSaving ] = useState(false);
  const { brands, userCreatedBrands, collections, account, seasons, selectedStore, selectedSeason, selectedCollection, sortByDisplayOrder, notify } = useContext(AuthContext)
  const [ availableItems, setAvailableItems ] = useState({})
  const [ availableBrands, setAvailableBrands ] = useState([])
  const [ brand, setBrand ] = useState(ALL_BRANDS)
  const [ newStore, setNewStore ] = useState(selectedStore)
  const [ newSeason, setNewSeason ] = useState(selectedSeason)
  const [ newCollection, setNewCollection ] = useState(selectedCollection)
  const [ downloadImages, setDownloadImages ] = useState(false)
  const [ downloadURL, setDownloadURL ] = useState(false)
  const [ downloadPhotoURL, setDownloadPhotoURL ] = useState(false)

  useEffect(() => {
    if (Object.keys(seasons).sort((a, b) => seasons[b].displayOrder - seasons[a].displayOrder).slice(0, seasonLimit).indexOf(selectedSeason) === -1) {
      setSeasonLimit(Object.keys(seasons).length);
    }
  }, [])
  
  useEffect(() => {
    let items_ref = ref(db, `items/${newStore}/${newSeason}`)
    setSaving(false)
    setDownloadURL(false)
    setDownloadPhotoURL(false)
    setBrand(ALL_BRANDS)
    return onValue(items_ref, (snapshot) => {
      const data = snapshot.val()
      setAvailableItems(data)
    })
  }, [open, newStore, newSeason, newCollection])

  useEffect(() => {
    let b = Object.keys(availableItems || {}).filter((e) => {     
      return newCollection === 'all' || availableItems[e]?.collectionId === newCollection
    }).map(e => availableItems[e]?.brand)

    setAvailableBrands(
      [
        ALL_BRANDS, 
        ...[...new Set(b)].map((e) => ({
          type: userCreatedBrands?.[e] ? 'Personal' : 'All brands',
          label: (userCreatedBrands?.[e]?.displayName) || (brands?.[e]?.displayName) || "",
          id: e,
        })).sort(sortByTypeAndLabel)
      ]
    )
  }, [availableItems])

  useEffect(() => {
    setNewStore(selectedStore)
    setNewSeason(selectedSeason)
    setNewCollection(selectedCollection)
  }, [open])

  const handleOpen = () => {
    Tracking.track('Portal - Export to excel')
    setOpen(true);
  }
  const handleClose = () => {
    setOpen(false);
    setSaving(false);
    if (onClose) {
      onClose();
    }
  }

  const handleSave = () => {
    setDownloadURL(false)

    let payload = {
      season: newSeason,
      storeId: newStore,
      brand: ALL_BRANDS.id === brand.id ? '0' : brand.id,
    }

    if (newCollection !== 'all') {
      payload.collection = newCollection
    }

    console.log(payload)
    
    get_csv_data(payload).then((result) => {
      if (result?.data?.download_url) {
        setDownloadURL(result?.data?.download_url)
      }
    }).catch((err) => {
      notify({
        message: err?.message || 'Something went wrong while trying to export CSV data',
        variant: 'error'
      })
    })

    if (downloadImages) {
      create_zip(payload).then((result) => {
        if (result?.data?.download_url) {
          setDownloadPhotoURL(result?.data?.download_url)
        }
      }).catch((err) => {
      notify({
        message: err?.message || 'Something went wrong while trying to download product images',
        variant: 'error'
      })
    })
    }
  }
  
  return (
    <>
      <SideNavItem
        onClick={handleOpen}
        active={false}
        external={false}
        disabled={false}
        icon={<DownloadIcon fontSize="small"/>}
        key={`sidenavitem-export`}
        title={'Export to Excel'}
      />
      <Dialog sx={{ maxWidth: '540px', m: 'auto' }} onKeyDown={stopPropagationForTab} open={open} onClose={handleClose}>
        <DialogTitle>Export to Excel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Export your products to a CSV file, compatible with Excel and other programs
          </DialogContentText>
          {/* Store selection */}
          <FormControl fullWidth variant="standard" sx={{ m: 1, mt: 5, minWidth: 120 }}>
            <InputLabel id="export-store">Store</InputLabel>
            <Select
              SelectDisplayProps={{ style: { paddingLeft: '0' } }}
              labelid="export-store"
              value={newStore}
              onChange={(e) => setNewStore(e.target.value)}
              label="Store"
            >
              {Object.keys(account.acctStores).map((s) => <MenuItem key={`store-${account.acctStores[s].displayName}`} value={s}>{account.acctStores[s].displayName}</MenuItem>)}
            </Select>  
          </FormControl>

          {/* Season selection */}
          <FormControl fullWidth variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <InputLabel id="export-season">Season</InputLabel>
            <Select
              SelectDisplayProps={{ style: { paddingLeft: '0' } }}
              labelid="export-season"
              value={newSeason}
              onChange={(e) => setNewSeason(e.target.value)}
              label="Season"
            >
              {Object.keys(seasons).sort((a, b) => seasons[b].displayOrder - seasons[a].displayOrder).slice(0, seasonLimit).map((s) => <MenuItem key={`season-${seasons[s].displayName}`} value={s}>{seasons[s].displayName}</MenuItem>)}
              {seasonLimit === 3 && <MenuItem onClickCapture={(e) => { setSeasonLimit(Object.keys(seasons).length); e.stopPropagation(); }} key={`season-show-more`} value={'show_more'}>Show more</MenuItem>}
            </Select>
          </FormControl>

          {/* Collection selection */}
          <FormControl fullWidth variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <InputLabel id="export-collection">Collection</InputLabel>
            <Select
              SelectDisplayProps={{ style: { paddingLeft: '0' } }}
              labelid="export-collection"
              value={newCollection}
              onChange={(e) => setNewCollection(e.target.value)}
              label="Collection"
            >
              {Object.keys(collections).filter(e => e !== '-').map(e => ({ ...collections[e], id: e })).sort(sortByDisplayOrder).map((s) => <MenuItem key={`collection-${s.displayName}`} value={s.id}>{s.displayName}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Brand selection */}
          <FormControl fullWidth variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <Autocomplete
              labelid="export-brand"
              value={brand}
              onChange={(event, value) => { setBrand(value) }}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id
              }}
              groupBy={(option) => option.type}
              options={availableBrands}
              onBlur={() => {
                if (!brand) {
                  setBrand(ALL_BRANDS)
                }
              }}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} variant='standard' label="Brand" InputLabelProps={{ shrink: true }} />}
            />
          </FormControl>

          {/* Export images */}
          <FormControl fullWidth variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <CheckListItem id="export_images" onChange={setDownloadImages} title={'Download Photo Archive'} value={downloadImages} />
          </FormControl>
          
          
          {downloadURL && (
            <FormControl fullWidth variant="standard" sx={{ m: 1, minWidth: 120 }}>
              <Button color="secondary" variant='contained' onClick={() => {
                window.open(downloadURL, '_blank').focus();
              }}>Download CSV Data</Button>
            </FormControl>
          )}
          
          {downloadPhotoURL && (
            <FormControl fullWidth variant="standard" sx={{ m: 1, minWidth: 120 }}>
              <Button color="secondary" variant='contained' onClick={() => {
                window.open(downloadPhotoURL, '_blank').focus();
              }}>Download Photo Archive</Button>
            </FormControl>
          )}
          
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button disabled={saving || !brand || downloadURL} onClick={handleSave}>Export</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}